import { Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { from, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { CampaignInfo } from '../../../assets/js/com/ts_api_client';
import { BdoApiService } from './bdo-api.service';
import { CustomerStoreService } from './customer-store.service';
import { TRACKING } from '../enums/trackingParts.enum';
import { TrackingService } from './tracking.service';
import { StorageService } from './storage.service';
import { TranslateService } from '@ngx-translate/core';
import { DatadogService } from './datadog.service';
import { VERBRAUCHSTYP } from '../enums/verbrauchstyp.enum';
import { VerbrauchstypPipe } from '../pipes/verbrauchstyp.pipe';
import { TARIFF_CONTEXT } from '../enums/tariffContext.enum';

export class CurrentCampaign {
  info: CampaignInfo;
  dismissed: boolean;
  linkToTariff: string;
  config: CampaignConfig;

  constructor(
    info: CampaignInfo,
    dismissed: boolean,
    linkToTariff: string,
    config: CampaignConfig
  ) {
    this.info = info;
    this.dismissed = dismissed;
    this.linkToTariff = linkToTariff;
    this.config = config;
  }

  toString() {
    try {
      return `BannerId: ${this.info.bannerId}, CampaignId: ${this.info.campaignId}, AccountId: ${this.info.accountId}, ProductId: ${this.info.productIds}, DivisionId: ${this.info.divisionId}`;
    } catch (error) {
      return '';
    }
  }
}

export interface CampaignConfig {
  textHeadline: string;
  textDescription: string;
  mainLogoSrc: string;
  mainLogoAlt: string;
}

@Injectable({
  providedIn: 'root'
})
export class CampaignService {
  public currentCampaign$: Observable<CurrentCampaign>;
  public currentCampaigns$: Observable<Array<CurrentCampaign>>;
  public hasNoCampaigns$: Observable<boolean>;
  private chargeFlexBannerId = 'RE_STROM_CFLX';
  private keepChargeFlexBannerId = 'RE_S4_CFLX_CFLX';
  private newTariffBannerIds = ['REXSELLGAS', 'REXSELLSTROM']; // Campaigns that lead to a new tariff instead of contract change

  constructor(private apiService: BdoApiService,
              private trackingService: TrackingService,
              private translateService: TranslateService,
              private customerStoreService: CustomerStoreService,
              private datadogService: DatadogService,
              private router: Router,
              private verbrauchstypPipe: VerbrauchstypPipe
  ) {


    this.currentCampaigns$ = this.customerStoreService?.currentAccountId$?.pipe(
      distinctUntilChanged(),
      switchMap((accountId) => {
        if (!accountId) { // Reset campaigns if logging out
          return of(undefined);
        } else {
          return this.apiService.getCampaignInfos(accountId).pipe(
            map((campaignInfos) => {
              return campaignInfos.map((campaignInfo) => ({
                info: campaignInfo,
                dismissed: this.isCampaignDismissed(campaignInfo),
                linkToTariff: this.buildLinkToTariffAgreement(campaignInfo),
                config: this.getCampaignConfig(campaignInfo)
              }));
            }),
            catchError((error: unknown) => {
              return of(null);
            })
          );

        }
      }),
      filter((campaigns) => !!campaigns),
      shareReplay(1)
    );

    this.hasNoCampaigns$ = this.currentCampaigns$?.pipe(
      map((campaigns) => campaigns.length === 0)
    );


      /**
     * return first non dismissed, if all are dismissed, return first campaign in array,
     * because the dashboard and the campaign popup both show one campaign, but the popup is dismissable
     * while the dashboard tile is not. The dashboard shows the same first non-dismissed campaign as the popup
     */
    this.currentCampaign$ = this.currentCampaigns$?.pipe(
      map((campaigns) => {
      const firstNonDismissedCampaign = campaigns.find((campaign) => campaign.dismissed !== true);
      this.datadogService.trackCustomEvent('CurrentCampaign$: ' + campaigns.map((campaign) => campaign?.toString()).join(', ') + ' # FirstNonDismissed: ' + firstNonDismissedCampaign?.toString());
      return firstNonDismissedCampaign ? firstNonDismissedCampaign : campaigns[0];
    }));
  }

  public isCampaignDismissed(campaign: CampaignInfo) {
    const storedCampaigns = StorageService.getCampaignsData();
    if (!storedCampaigns) {
      return false;
    }
    return storedCampaigns?.some((item) =>
      item.accountId === campaign.accountId &&
      item.bannerId === campaign.bannerId);
  }

  dismissCampaign(campaign: CurrentCampaign) {
    StorageService.addCampaign(campaign.info);
  }

  goToCampaign(customCampaign?: CurrentCampaign): Observable<boolean> {
    StorageService.clearOldContract();
    StorageService.clearTariffData();
    StorageService.setTariffContext(TARIFF_CONTEXT.CHANGE_CAMPAIGN);
    if (customCampaign){
      return from(this.router.navigate([customCampaign.linkToTariff]).then(() => true));
    }
    return this.currentCampaign$.pipe(
      tap((campaign) => {
        if (campaign) {
          this.trackingService.postSimpleTracking(TRACKING.LOCATION.CAMPAIGN, TRACKING.ACTION.GOTO + ' Offer ' + campaign.info.bannerId);
          StorageService.clearOldContract();
          StorageService.clearTariffData();
          if (campaign.info?.bannerId === this.chargeFlexBannerId || campaign.info?.bannerId === this.keepChargeFlexBannerId) {
            StorageService.setChargeFlexContext();
          }
        }
      }),
      switchMap((campaign) => {
        if (campaign) {
          const navigationExtras: NavigationExtras = {};
          const url = new URL(campaign.linkToTariff, window.location.origin);
          if (url.searchParams.has('bannerId')) {
            navigationExtras.queryParams = { bannerId: campaign.info.bannerId };
          }
          return this.router.navigate([url.pathname], navigationExtras).then(() => true);
        } else {
          return of(false);
        }
      })
    );
  }

  buildLinkToTariffAgreement(campaign: CampaignInfo) {
    if (campaign.bannerId === this.chargeFlexBannerId || campaign.bannerId === this.keepChargeFlexBannerId) {
      return  'vertrag/' + campaign.accountId + '/anpassen/zaehlerauswahl'; // TODO: Check if bannerId needed for chargeFlex campaign
    } else if (this.newTariffBannerIds.includes(campaign.bannerId)) {
      return  'anmelden/' + campaign.accountId + '/neu/adresse?bannerId=' + campaign.bannerId;
    } else {
      return  'vertrag/' + campaign.accountId + '/anpassen/tarifauswahl?bannerId=' + campaign.bannerId;
    }
  }


  getCampaignConfig(campaign: CampaignInfo): CampaignConfig {
    const defaultConfig: CampaignConfig = {
      textHeadline: this.translateService.instant('campaign.default.title'),
      textDescription: this.translateService.instant('campaign.default.description'),
      mainLogoSrc: this.translateService.instant('campaign.default.logo'),
      mainLogoAlt: this.translateService.instant('campaign.default.logoAlt')
    };

    const configs: Array<{ bannerIds: Array<string>, config: CampaignConfig }> = [

      {
        bannerIds: [
          'RE_GAS_MVP_2',
          'RE_GAS_TMVP_2',
        ],
        config: {
          textHeadline: this.translateService.instant('campaign.newPriceGuaranteeG6.title'),
          textDescription: this.translateService.instant('campaign.newPriceGuaranteeG6.description'),
          mainLogoSrc: this.translateService.instant('campaign.newPriceGuaranteeG6.logo'),
          mainLogoAlt: this.translateService.instant('campaign.newPriceGuaranteeG6.logoAlt')
        }
      },
      {
        bannerIds: [
          'RE_STROM_MVP_2',
          'RE_STROM_TMVP_2'
        ],
        config: {
          textHeadline: this.translateService.instant('campaign.newPriceGuaranteeS4.title'),
          textDescription: this.translateService.instant('campaign.newPriceGuaranteeS4.description'),
          mainLogoSrc: this.translateService.instant('campaign.newPriceGuaranteeS4.logo'),
          mainLogoAlt: this.translateService.instant('campaign.newPriceGuaranteeS4.logoAlt')
        }
      },
      {
        bannerIds: [
          'CSCORE-REDS',
          'CSCORE-REDG'
        ],
        config: {
          textHeadline: this.translateService.instant('campaign.cscore.red.title'),
          textDescription: this.translateService.instant('campaign.cscore.red.description'),
          mainLogoSrc: this.translateService.instant('campaign.default.logo'),
          mainLogoAlt: this.translateService.instant('campaign.default.logoAlt')
        }
      },
      {
        bannerIds: [
          'CSCORE-YELLOWS'
        ],
        config: {
          textHeadline: this.translateService.instant('campaign.cscore.yellow.title'),
          textDescription: this.translateService.instant('campaign.cscore.yellow.description'),
          mainLogoSrc: this.translateService.instant('campaign.default.logo'),
          mainLogoAlt: this.translateService.instant('campaign.default.logoAlt')
        }
      },
      {
        bannerIds: [
          'CSCORE-YELLOWG'
        ],
        config: {
          textHeadline: this.translateService.instant('campaign.cscore.yellow.title'),
          textDescription: this.translateService.instant('campaign.cscore.yellowGas.description'),
          mainLogoSrc: this.translateService.instant('campaign.default.logo'),
          mainLogoAlt: this.translateService.instant('campaign.default.logoAlt')
        }
      },
      {
        bannerIds: [
          'CSCORE-GREENS'
        ],
        config: {
          textHeadline: this.translateService.instant('campaign.cscore.green.title'),
          textDescription: this.translateService.instant('campaign.cscore.green.description'),
          mainLogoSrc: this.translateService.instant('campaign.default.logo'),
          mainLogoAlt: this.translateService.instant('campaign.default.logoAlt')
        }
      },
      {
        bannerIds: [
          'CSCORE-GREENG'
        ],
        config: {
          textHeadline: this.translateService.instant('campaign.cscore.green.title'),
          textDescription: this.translateService.instant('campaign.cscore.greenGas.description'),
          mainLogoSrc: this.translateService.instant('campaign.default.logo'),
          mainLogoAlt: this.translateService.instant('campaign.default.logoAlt')
        }
      },
      {
        bannerIds: [
          'RE_WP_WPE'
        ],
        config: {
          textHeadline: this.translateService.instant('campaign.newHeatpump.title'),
          textDescription: this.translateService.instant('campaign.newHeatpump.description'),
          mainLogoSrc: this.translateService.instant('campaign.newHeatpump.logo'),
          mainLogoAlt: this.translateService.instant('campaign.newHeatpump.logoAlt')
        }
      },
      {
        bannerIds: [
          'RE_S_KOMBI12'
        ],
        config: {
          textHeadline: this.translateService.instant('campaign.newNightStorage.title'),
          textDescription: this.translateService.instant('campaign.newNightStorage.description'),
          mainLogoSrc: this.translateService.instant('campaign.newPriceGuaranteeS4.logo'),
          mainLogoAlt: this.translateService.instant('campaign.newPriceGuaranteeS4.logoAlt')
        }
      },
      {
        bannerIds: [
          'REXSELLGAS', 'REXSELLSTROM'
        ],
        config: {
          textHeadline: this.translateService.instant('campaign.crossSelling.title'),
          textDescription: this.translateService.instant('campaign.crossSelling.description' ,
            { division: [this.verbrauchstypPipe.transform(campaign.bannerId === 'REXSELLGAS' ? VERBRAUCHSTYP.Gas : VERBRAUCHSTYP.Strom)] }),
          mainLogoSrc: this.translateService.instant('campaign.crossSelling.logo'),
          mainLogoAlt: this.translateService.instant('campaign.crossSelling.logoAlt')
        }
      },
      {
        bannerIds: ['CANCEL'],
        config: {
          textHeadline: this.translateService.instant('campaign.cancel.title'),
          textDescription: this.translateService.instant('campaign.cancel.description'),
          mainLogoSrc: this.translateService.instant('campaign.cancel.logo'),
          mainLogoAlt: this.translateService.instant('campaign.cancel.logoAlt')
        }
      },
      {
        bannerIds: [
          this.chargeFlexBannerId
        ],
        config: {
          textHeadline: this.translateService.instant('campaign.newChargeFlex.title'),
          textDescription: this.translateService.instant('campaign.newChargeFlex.description'),
          mainLogoSrc: this.translateService.instant('campaign.newChargeFlex.logo'),
          mainLogoAlt: this.translateService.instant('campaign.newChargeFlex.logoAlt')
        }
      },
      {
        bannerIds: [
          this.keepChargeFlexBannerId
        ],
        config: {
          textHeadline: this.translateService.instant('campaign.keepChargeFlex.title'),
          textDescription: this.translateService.instant('campaign.keepChargeFlex.description'),
          mainLogoSrc: this.translateService.instant('campaign.keepChargeFlex.logo'),
          mainLogoAlt: this.translateService.instant('campaign.keepChargeFlex.logoAlt')
        }
      },
    ];
    const configForBannerId = configs.find((config) => config.bannerIds.includes(campaign.bannerId));
    if (!configForBannerId) {
      return defaultConfig;
    } else {
      return configForBannerId.config;
    }
  }

}
